import {
  PersistentData,
  PersistentUserCatalogNoticeState,
  PersistentUserCatalogSearchState,
  PersistentUserCatalogTodayState,
  PersistentUserLifeSupportsTipHistoryState,
  PersistentUserNewspaperState,
  PersistentUserNotificationState,
  PersistentUserReactionState
} from './PersistentData';

declare let window: Window & {
  storePersistentData?: (jsonString: string) => void;
  onPersistentDataStore?: (jsonString: string) => void;
  restorePersistentData?: () => void;
  onPersistentDataRestore?: (jsonString: string) => void;
};

function storePersistentData(jsonString: string): Promise<string | undefined> {
  if (!window.storePersistentData) {
    return Promise.resolve(undefined);
  }
  const p = new Promise<string | undefined>(resolve => {
    window.onPersistentDataStore = (stored: string) => {
      resolve(stored);
    };
  });
  window.storePersistentData(jsonString);
  return p;
}

function restorePersistentData(): Promise<PersistentData> {
  if (!window.restorePersistentData) {
    if (process.env.NODE_ENV !== 'production') {
      return Promise.resolve({});
    } else {
      throw new Error('window.restorePersistentData is not available');
    }
  }
  const p = new Promise<any>(resolve => {
    window.onPersistentDataRestore = jsonString => {
      if (!jsonString) {
        resolve({});
      } else {
        try {
          const parsed = JSON.parse(jsonString);
          resolve(parsed);
        } catch (err) {
          console.error(err);
          resolve({});
        }
      }
    };
  }).catch(() => ({}));
  window.restorePersistentData();
  return p;
}

async function addPersistentData(data: Partial<PersistentData>) {
  const restored = await restorePersistentData();
  const updated: PersistentData = { ...restored, ...data };
  return storePersistentData(JSON.stringify(updated));
}

async function persistUserNotificationState(
  userId: string,
  data: Partial<PersistentUserNotificationState>
) {
  const restored = await restorePersistentData();

  const current = restored.userNotificationStates || {};
  const userNotificationState = current[userId] || {};
  const nextState = { ...userNotificationState, ...data };

  const updated: PersistentData = {
    ...restored,
    userNotificationStates: {
      ...current,
      [userId]: nextState
    }
  };

  return storePersistentData(JSON.stringify(updated));
}

async function persistUserReactionState(
  userId: string,
  history: PersistentUserReactionState['history']
) {
  const restored = await restorePersistentData();

  const current = restored.userReactionStates || {};
  const userReactionState = current[userId] || {};
  const nextState = { ...userReactionState, history };

  const updated: PersistentData = {
    ...restored,
    userReactionStates: {
      ...current,
      ...{ [userId]: nextState }
    }
  };

  return storePersistentData(JSON.stringify(updated));
}

async function persistUserCatalogTodayState(
  userId: string,
  today: PersistentUserCatalogTodayState
) {
  const restored = await restorePersistentData();

  const current = restored.userCatalogTodayStates || {};

  const updated: PersistentData = {
    ...restored,
    userCatalogTodayStates: {
      ...current,
      ...{ [userId]: today }
    }
  };

  return storePersistentData(JSON.stringify(updated));
}

async function persistUserCatalogSearchHistoryState(
  userId: string,
  history: PersistentUserCatalogSearchState['history']
) {
  const restored = await restorePersistentData();

  const current = restored.userCatalogSearchStates || {};
  const userCatalogState = current[userId] || {};
  const nextState = { ...userCatalogState, history };

  const updated: PersistentData = {
    ...restored,
    userCatalogSearchStates: {
      ...current,
      ...{ [userId]: nextState }
    }
  };

  return storePersistentData(JSON.stringify(updated));
}

async function persistUserCatalogNoticeState(
  userId: string,
  noticeFlags: PersistentUserCatalogNoticeState
) {
  const restored = await restorePersistentData();

  const current = restored.userCatalogNoticeStates || {};
  const userCatalogNoticeState = current[userId] || {};
  const nextState = { ...userCatalogNoticeState, ...noticeFlags };

  const updated: PersistentData = {
    ...restored,
    userCatalogNoticeStates: {
      ...current,
      ...{ [userId]: nextState }
    }
  };

  return storePersistentData(JSON.stringify(updated));
}

async function persistUserLifeSupportsTipHistoryState(
  userId: string,
  tipHistory: PersistentUserLifeSupportsTipHistoryState
) {
  const restored = await restorePersistentData();

  const current = restored.userLifeSupportsTipHistoryStates || {};
  const userLifeSupportsTipHistoryState = current[userId] || {};
  const nextState = { ...userLifeSupportsTipHistoryState, ...tipHistory };

  const updated: PersistentData = {
    ...restored,
    userLifeSupportsTipHistoryStates: {
      ...current,
      ...{ [userId]: nextState }
    }
  };

  return storePersistentData(JSON.stringify(updated));
}

async function persistUserNewspaperState(userId: string, state: PersistentUserNewspaperState) {
  const restored = await restorePersistentData();
  const current = restored.userNewspaperStates || {};
  const userNewspaperState = current[userId] || {};
  const nextState = { ...userNewspaperState, ...state };

  const updated: PersistentData = {
    ...restored,
    userNewspaperStates: {
      ...current,
      ...{ [userId]: nextState }
    }
  };

  return storePersistentData(JSON.stringify(updated));
}

export {
  storePersistentData,
  restorePersistentData,
  addPersistentData,
  persistUserNotificationState,
  persistUserReactionState,
  persistUserCatalogTodayState,
  persistUserCatalogSearchHistoryState,
  persistUserCatalogNoticeState,
  persistUserLifeSupportsTipHistoryState,
  persistUserNewspaperState
};
